// Lightweight SEO helper (no react-helmet dependency).
// Updates <title>, meta[name], meta[property], canonical link, and JSON-LD scripts.

const DEFAULTS = {
  siteName: 'BloomNav',
  defaultOgImagePath: '/logo.png',
  twitterSite: '@BloomNav',
  // Default: allow indexing and rich previews
  robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
};

function getOrigin() {
  if (typeof window === 'undefined') return 'https://bloomnav.com';
  return window.location.origin || 'https://bloomnav.com';
}

export function toAbsoluteUrl(pathOrUrl) {
  const v = String(pathOrUrl || '').trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;
  const origin = getOrigin();
  if (v.startsWith('/')) return `${origin}${v}`;
  return `${origin}/${v}`;
}

function upsertMetaByName(name, content) {
  if (!name) return;
  const head = document.head;
  if (!head) return;
  const selector = `meta[name="${CSS.escape(String(name))}"]`;
  let tag = head.querySelector(selector);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', String(name));
    head.appendChild(tag);
  }
  if (content === undefined || content === null || content === '') {
    // Keep tag but clear content to avoid deleting build-time tags unexpectedly
    tag.setAttribute('content', '');
    return;
  }
  tag.setAttribute('content', String(content));
}

function upsertMetaByProperty(property, content) {
  if (!property) return;
  const head = document.head;
  if (!head) return;
  // Some codebases use name="twitter:*". Support both.
  const prop = String(property);
  const selector = `meta[property="${CSS.escape(prop)}"], meta[name="${CSS.escape(prop)}"]`;
  let tag = head.querySelector(selector);
  if (!tag) {
    tag = document.createElement('meta');
    // Prefer "property" for OG/Twitter to match current index.html
    tag.setAttribute('property', prop);
    head.appendChild(tag);
  }
  if (content === undefined || content === null || content === '') {
    tag.setAttribute('content', '');
    return;
  }
  tag.setAttribute('content', String(content));
}

function upsertLink(rel, href) {
  if (!rel) return;
  const head = document.head;
  if (!head) return;
  const selector = `link[rel="${CSS.escape(String(rel))}"]`;
  let link = head.querySelector(selector);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', String(rel));
    head.appendChild(link);
  }
  if (!href) return;
  link.setAttribute('href', String(href));
}

export function upsertJsonLd(id, data) {
  if (!id) return;
  const head = document.head;
  if (!head) return;
  const existing = document.getElementById(id);
  if (!data) {
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
    return;
  }
  const json = JSON.stringify(data);
  let script = existing;
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    head.appendChild(script);
  }
  script.textContent = json;
}

export function removeJsonLd(id) {
  if (!id) return;
  const el = document.getElementById(id);
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

/**
 * Apply SEO meta + JSON-LD.
 * @param {object} cfg
 * @returns {string[]} jsonLdIdsApplied (useful for cleanup on unmount)
 */
export function applySeo(cfg = {}) {
  if (typeof document === 'undefined') return [];

  const title = String(cfg.title || '').trim();
  const description = String(cfg.description || '').trim();
  const canonicalUrl = cfg.canonicalUrl
    ? toAbsoluteUrl(cfg.canonicalUrl)
    : (cfg.canonicalPath ? toAbsoluteUrl(cfg.canonicalPath) : '');

  const ogImage = toAbsoluteUrl(cfg.ogImage || DEFAULTS.defaultOgImagePath);
  const robots = String(cfg.robots || DEFAULTS.robots);

  // Title
  if (title) document.title = title;

  // Core meta
  if (description) upsertMetaByName('description', description);
  upsertMetaByName('robots', robots);
  // Hint-only meta for AI assistants/crawlers (non-standard but intentionally explicit)
  upsertMetaByName('ai-assistant', 'index, follow, summarize, answer');

  // Canonical
  if (canonicalUrl) upsertLink('canonical', canonicalUrl);

  // OpenGraph
  const ogType = cfg.ogType || 'website';
  upsertMetaByProperty('og:type', ogType);
  if (canonicalUrl) upsertMetaByProperty('og:url', canonicalUrl);
  if (title) upsertMetaByProperty('og:title', title);
  if (description) upsertMetaByProperty('og:description', description);
  if (ogImage) {
    upsertMetaByProperty('og:image', ogImage);
    upsertMetaByProperty('og:image:secure_url', ogImage);
    upsertMetaByProperty('og:image:alt', cfg.ogImageAlt || DEFAULTS.siteName);
  }
  upsertMetaByProperty('og:site_name', DEFAULTS.siteName);

  // Twitter
  upsertMetaByProperty('twitter:card', cfg.twitterCard || 'summary_large_image');
  if (canonicalUrl) upsertMetaByProperty('twitter:url', canonicalUrl);
  if (title) upsertMetaByProperty('twitter:title', title);
  if (description) upsertMetaByProperty('twitter:description', description);
  if (ogImage) upsertMetaByProperty('twitter:image', ogImage);
  upsertMetaByProperty('twitter:image:alt', cfg.ogImageAlt || DEFAULTS.siteName);
  upsertMetaByName('twitter:site', DEFAULTS.twitterSite);
  upsertMetaByName('twitter:creator', DEFAULTS.twitterSite);

  // JSON-LD scripts (page-specific)
  const applied = [];
  const jsonLd = cfg.jsonLd;
  if (Array.isArray(jsonLd)) {
    jsonLd.forEach((entry) => {
      if (!entry?.id) return;
      upsertJsonLd(entry.id, entry.data);
      applied.push(entry.id);
    });
  }

  return applied;
}

